import { DEFAULT_RETAIL_PRICE_LIST, DEFAULT_ROOF_CLOSING, DEFAULT_ROOF_STEPS, type RetailAddOn, type RetailPriceList, type RetailProduct, describedAs } from "@nmt/shared";
import { useEffect, useState } from "react";
import { errorMessage } from "../../api";
import { Alert, Button, Checkbox, Field, Loading, PageHeader, Panel, PanelHeader, SelectField, TextAreaField, parseDollars } from "../../components/ui";
import { useSettings } from "./useSettings";

type Row = {
  key: string;
  name: string;
  unit: RetailAddOn["unit"];
  price: string;
  cost: string;
  description: string;
  /** Shingles: bundles per square, and feet per bundle of ridge cap and starter. */
  bundles: string;
  ridge: string;
  starter: string;
  /** Square add-ons: grow with the roof's material squares. */
  scaled: boolean;
};

const toRow = (x: RetailProduct | RetailAddOn): Row => ({
  key: x.key,
  name: x.name,
  unit: "unit" in x ? x.unit : "SQ",
  price: (x.priceCents / 100).toFixed(2),
  cost: (x.costCents / 100).toFixed(2),
  description: describedAs("unit" in x ? "addOn" : "product", x.key, x.description ?? ""),
  bundles: String("bundlesPerSquare" in x ? x.bundlesPerSquare : 3),
  ridge: String("ridgeLfPerBundle" in x ? x.ridgeLfPerBundle : 30),
  starter: String("starterLfPerBundle" in x ? x.starterLfPerBundle : 100),
  scaled: "scaled" in x ? x.scaled : true,
});

/** A key for a new item, from its name, not clashing with the others (proposals keep the key). */
function keyFor(name: string, taken: Set<string>) {
  const base = name.toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 30) || "item";
  let k = base;
  for (let i = 2; taken.has(k); i++) k = `${base}${i}`;
  return k;
}

const whole = (t: string, min: number, max: number) => (/^\d+$/.test(t.trim()) && Number(t) >= min && Number(t) <= max ? Number(t) : null);

/** Settings → Retail pricing: the shingle systems and add-ons reps price roofs from, what the customer reads about them, and how we install a roof. */
export function RetailPricing() {
  const { query, save } = useSettings();
  const [products, setProducts] = useState<Row[] | null>(null);
  const [addOns, setAddOns] = useState<Row[]>([]);
  const [minMargin, setMinMargin] = useState("");
  const [steps, setSteps] = useState("");
  const [closing, setClosing] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    const l = query.data?.settings["retail.priceList"];
    if (l && products === null) {
      setProducts(l.products.map(toRow));
      setAddOns(l.addOns.map(toRow));
      setMinMargin(String(l.minMarginPercent));
      setSteps(l.roofSteps.join("\n"));
      setClosing(l.roofClosing);
    }
  }, [query.data, products]);

  if (query.isLoading || products === null) return <Loading />;

  const submit = () => {
    setNotice(null);
    setProblem(null);
    const cents = (r: Row) => ({ price: parseDollars(r.price), cost: parseDollars(r.cost) });
    const bad = [...products, ...addOns].find((r) => !r.name.trim() || [cents(r).price, cents(r).cost].some((c) => c === null || Number.isNaN(c)));
    if (bad) return setProblem(`Check "${bad.name || "the unnamed row"}": it needs a name, a price, and a cost.`);
    const badCover = products.find((r) => whole(r.bundles, 1, 10) === null || whole(r.ridge, 1, 500) === null || whole(r.starter, 1, 500) === null);
    if (badCover) return setProblem(`Check "${badCover.name}": bundles per square (1–10) and feet per bundle of ridge and starter are whole numbers.`);
    const margin = Number(minMargin);
    if (!Number.isInteger(margin) || margin < 0 || margin > 90) return setProblem("The minimum margin is a whole percent, 0 to 90.");
    const list: RetailPriceList = {
      products: products.map((r) => ({ key: r.key, name: r.name.trim(), priceCents: cents(r).price!, costCents: cents(r).cost!, bundlesPerSquare: whole(r.bundles, 1, 10)!, ridgeLfPerBundle: whole(r.ridge, 1, 500)!, starterLfPerBundle: whole(r.starter, 1, 500)!, description: r.description.trim() })),
      addOns: addOns.map((r) => ({ key: r.key, name: r.name.trim(), unit: r.unit, priceCents: cents(r).price!, costCents: cents(r).cost!, scaled: r.unit === "SQ" && r.scaled, description: r.description.trim() })),
      minMarginPercent: margin,
      roofSteps: steps.split("\n").map((s) => s.trim()).filter(Boolean),
      roofClosing: closing.trim(),
    };
    save.mutate({ "retail.priceList": list }, { onSuccess: () => setNotice("Saved. New prices apply to drafts and new proposals; sent proposals keep theirs. New roof proposals start with these steps.") });
  };

  const taken = new Set([...products, ...addOns].map((r) => r.key));
  const blank = (key: string): Row => ({ key, name: "", unit: "SQ", price: "", cost: "", description: "", bundles: "3", ridge: "30", starter: "100", scaled: true });
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Retail pricing"
        description="What reps price retail roofs from, and what the customer reads. A roof is priced by the bundles it takes (field shingles with waste, plus ridge cap and starter), at the price per square ÷ bundles per square. Costs are ours and only show as the margin on the rep's screen."
      />
      {save.error && <Alert>{errorMessage(save.error)}</Alert>}
      {problem && <Alert>{problem}</Alert>}
      {notice && <Alert tone="ok">{notice}</Alert>}
      <Rows title="Shingle systems" description="Price and cost per square. Bundles per square and how many feet one bundle of ridge cap and of starter covers set the bundle count." rows={products} setRows={setProducts} kind="product" onAdd={() => setProducts([...products, blank(keyFor("product", taken))])} />
      <Rows title="Add-ons" description="Steep, two-story, extra layers, decking, vents... per square, each, or linear foot. A square add-on can grow with the roof (steep: entered as measured squares) or be priced on the squares entered (a redeck)." rows={addOns} setRows={setAddOns} kind="addOn" onAdd={() => setAddOns([...addOns, blank(keyFor("addon", taken))])} />
      <Panel>
        <PanelHeader
          title="How we install a roof"
          description="One step per line. New roof proposals start with these (the rep can change them on each). Shown under the scope of work."
          action={
            <Button variant="quiet" className="px-3 py-1.5" onClick={() => setSteps(DEFAULT_ROOF_STEPS.join("\n"))}>
              Back to the original
            </Button>
          }
        />
        <div className="px-5 py-4">
          <TextAreaField label="Steps" rows={12} value={steps} onChange={(e) => setSteps(e.target.value)} />
        </div>
      </Panel>
      <Panel>
        <PanelHeader
          title="Why NMT (closing paragraph)"
          description="The last part of a roof's scope. {years} becomes the workmanship warranty years (Settings → Lists and job numbers)."
          action={
            <Button variant="quiet" className="px-3 py-1.5" onClick={() => setClosing(DEFAULT_ROOF_CLOSING)}>
              Back to the original
            </Button>
          }
        />
        <div className="px-5 py-4">
          <TextAreaField label="Paragraph" rows={4} maxLength={3000} value={closing} onChange={(e) => setClosing(e.target.value)} />
        </div>
      </Panel>
      <Panel>
        <PanelHeader title="Margin warning" />
        <div className="flex flex-wrap items-end gap-4 px-5 py-5">
          <Field label="Warn below this margin (%)" type="number" min={0} max={90} value={minMargin} onChange={(e) => setMinMargin(e.target.value)} />
          <Button variant="primary" busy={save.isPending} onClick={submit}>
            Save pricing
          </Button>
        </div>
      </Panel>
    </div>
  );
}

function Rows({ title, description, rows, setRows, kind, onAdd }: { title: string; description: string; rows: Row[]; setRows: (r: Row[]) => void; kind: "product" | "addOn"; onAdd: () => void }) {
  const edit = (i: number, p: Partial<Row>) => setRows(rows.map((r, k) => (k === i ? { ...r, ...p } : r)));
  const original = (key: string) => (kind === "product" ? DEFAULT_RETAIL_PRICE_LIST.products : DEFAULT_RETAIL_PRICE_LIST.addOns).find((x) => x.key === key)?.description ?? "";
  return (
    <Panel>
      <PanelHeader
        title={title}
        description={description}
        action={
          <Button variant="secondary" className="px-3 py-1.5" onClick={onAdd}>
            Add
          </Button>
        }
      />
      <ul>
        {rows.map((r, i) => (
          <li key={r.key} className="flex flex-col gap-2 border-b border-mist px-5 py-4">
            <div className={kind === "addOn" ? "grid grid-cols-[1fr_80px_110px_110px_auto] items-end gap-2" : "grid grid-cols-[1fr_110px_110px_auto] items-end gap-2"}>
              <Field label="Name" value={r.name} maxLength={120} onChange={(e) => edit(i, { name: e.target.value })} />
              {kind === "addOn" && (
                <SelectField label="Per" value={r.unit} onChange={(e) => edit(i, { unit: e.target.value as Row["unit"] })}>
                  <option value="SQ">SQ</option>
                  <option value="EA">EA</option>
                  <option value="LF">LF</option>
                </SelectField>
              )}
              <Field label={kind === "product" ? "Price / SQ" : "Price"} inputMode="decimal" value={r.price} onChange={(e) => edit(i, { price: e.target.value })} />
              <Field label="Our cost" inputMode="decimal" value={r.cost} onChange={(e) => edit(i, { cost: e.target.value })} />
              <Button variant="quiet" className="mb-0.5" aria-label={`Remove ${r.name}`} onClick={() => setRows(rows.filter((_, k) => k !== i))}>
                ×
              </Button>
            </div>
            {kind === "product" && (
              <div className="grid grid-cols-3 gap-2 sm:max-w-md">
                <Field label="Bundles / SQ" inputMode="numeric" value={r.bundles} onChange={(e) => edit(i, { bundles: e.target.value })} />
                <Field label="Ridge LF / bundle" inputMode="numeric" value={r.ridge} onChange={(e) => edit(i, { ridge: e.target.value })} />
                <Field label="Starter LF / bundle" inputMode="numeric" value={r.starter} onChange={(e) => edit(i, { starter: e.target.value })} />
              </div>
            )}
            {kind === "addOn" && r.unit === "SQ" && <Checkbox label="Grows with the roof (enter measured squares; priced on material squares with waste, ridge, and starter)" checked={r.scaled} onChange={(v) => edit(i, { scaled: v })} />}
            <TextAreaField
              label={kind === "product" ? "What the customer reads about it" : "What the customer reads about it (optional; shown when it's on a proposal)"}
              rows={kind === "product" ? 3 : 2}
              maxLength={3000}
              value={r.description}
              onChange={(e) => edit(i, { description: e.target.value })}
            />
            {original(r.key) && r.description !== original(r.key) && (
              <button className="self-start text-xs font-semibold text-brand hover:underline" onClick={() => edit(i, { description: original(r.key) })}>
                Back to the original wording
              </button>
            )}
          </li>
        ))}
      </ul>
    </Panel>
  );
}
